import React, { useState, useEffect, useCallback } from 'react';
import FileDropZone from './shared/FileDropZone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, faUpload, faFileText, faSpinner, faRobot, faExclamationTriangle 
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../utils/axiosConfig';
import ContractExtractionResults from './ContractExtractionResults';
import { useFlashMessage } from './FlashMessage/FlashMessageContext';
import { getErrorMessage } from '../utils/errorMessages';
import { CATEGORIES } from '../data/productTaxonomy';

const ContractUploadModal = ({ isOpen, onClose, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [uploadState, setUploadState] = useState('idle'); // 'idle', 'extracting', 'results', 'error', 'manual_product_type', 'uploading_manual'
    const [error, setError] = useState('');
    const [extractedData, setExtractedData] = useState(null);
    const [selectedFields, setSelectedFields] = useState({});
    const [analysisId, setAnalysisId] = useState(null);
    const [reAnalysisGuidance, setReAnalysisGuidance] = useState('');
    const [showManualProductType, setShowManualProductType] = useState(false);
    const [selectedProductType, setSelectedProductType] = useState('');
    const [extractionStep, setExtractionStep] = useState(0);
    const [pageProgress, setPageProgress] = useState({ current: 0, total: 0 });
    const [extractionSteps] = useState([
        'Analyzing document format and structure...',
        'Converting document to processable format...',
        'Extracting text using advanced OCR and parsing...',
        'Applying AI analysis to identify contract terms...',
        'Extracting product specifications and pricing...',
        'Validating and structuring extracted data...',
        'Finalizing contract information extraction...'
    ]);
    // Flat list of every subtype for the manual product-type picker.
    const [productTypes] = useState(
        CATEGORIES.flatMap((cat) =>
            cat.subtypes.map((sub) => ({
                value: sub.value,
                label: `${cat.label} — ${sub.label}`,
            }))
        )
    );

    const { showError, showSuccess } = useFlashMessage();
    
    // Progress polling function
    const pollProgress = useCallback(async (analysisId) => {
        try {
            const response = await axiosInstance.get(`/api/stock/contracts/progress/${analysisId}/`);
            const { current_page, total_pages, is_complete } = response.data;
            
            setPageProgress({ current: current_page || 0, total: total_pages || 0 });
            
            return is_complete;
        } catch (error) {
            console.error('Progress polling error:', error);
            return false;
        }
    }, []);

    // Start progress polling when we have an analysis ID during extraction
    useEffect(() => {
        let progressInterval;
        
        if (uploadState === 'extracting' && analysisId) {
            // Start progress polling every 500ms for responsive updates
            progressInterval = setInterval(async () => {
                const isComplete = await pollProgress(analysisId);
                if (isComplete) {
                    clearInterval(progressInterval);
                }
            }, 500);
        }
        
        return () => {
            if (progressInterval) clearInterval(progressInterval);
        };
    }, [uploadState, analysisId, pollProgress]);

    // Cycle through extraction steps during processing with fade animation
    useEffect(() => {
        let interval;
        
        if (uploadState === 'extracting') {
            setExtractionStep(0);
            
            // Start step cycling
            interval = setInterval(() => {
                // Fade out current step
                const currentStepElement = document.querySelector('.current-step');
                if (currentStepElement) {
                    currentStepElement.style.opacity = '0';
                    
                    // After fade out, change text and fade in
                    setTimeout(() => {
                        setExtractionStep(prev => {
                            if (prev < extractionSteps.length - 1) {
                                return prev + 1;
                            }
                            return 0; // Loop back to start
                        });
                        
                        // Fade back in
                        setTimeout(() => {
                            if (currentStepElement) {
                                currentStepElement.style.opacity = '1';
                            }
                        }, 100);
                    }, 500); // Wait for fade out to complete
                }
            }, 3000); // Change step every 3 seconds
        }
        
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [uploadState, extractionSteps.length]);

    // Debug logging for modal state changes
    useEffect(() => {
        console.log('🔄 Modal state changed - showManualProductType:', showManualProductType);
        console.log('🔄 Current uploadState:', uploadState);
        console.log('🔄 Analysis ID:', analysisId);
    }, [showManualProductType, uploadState, analysisId]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setFile(selectedFile);
        setError('');
    };

    /* The drop zone itself is components/shared/FileDropZone. This is only
       what to do with the file once it arrives, so the file picker and the
       drop cannot end up disagreeing about it. */
    const acceptDroppedFile = (droppedFile) => {
        setFile(droppedFile);
        setError('');
    };

    const validateFile = (file) => {
        if (!file) {
            return 'Please select a file';
        }
        
        // Support all file types the document processor accepts
        const allowedTypes = [
            // PDF Documents
            'application/pdf',
            
            // Microsoft Office Documents
            'application/msword',                                                           // .doc
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',    // .docx
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',          // .xlsx
            'application/vnd.ms-excel',                                                    // .xls
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',  // .pptx
            'application/vnd.ms-powerpoint',                                               // .ppt
            
            // Text Documents
            'text/plain',                          // .txt
            'text/markdown',                       // .md
            'text/html',                          // .html, .htm
            'application/xhtml+xml',              // .xhtml
            'text/csv',                           // .csv
            'text/tab-separated-values',          // .tsv
            'text/asciidoc',                      // .asciidoc, .adoc
            'text/x-asciidoc',                    // .asciidoc
            'application/rtf',                    // .rtf
            'text/richtext',                      // .rtf
            
            // Image Documents (OCR capable)
            'image/png',                          // .png
            'image/jpeg',                         // .jpg, .jpeg
            'image/jpg',                          // .jpg
            'image/tiff',                         // .tiff, .tif
            'image/tif',                          // .tif
            'image/bmp',                          // .bmp
            'image/webp',                         // .webp
            'image/gif',                          // .gif
            'image/svg+xml',                      // .svg
            
            // Email Documents
            'message/rfc822',                     // .eml
            'application/vnd.ms-outlook',         // .msg
            
            // OpenDocument Formats
            'application/vnd.oasis.opendocument.text',         // .odt
            'application/vnd.oasis.opendocument.spreadsheet',  // .ods
            'application/vnd.oasis.opendocument.presentation', // .odp
            
            // Other Document Formats
            'application/xml',                    // .xml
            'text/xml',                          // .xml
            'application/json',                  // .json
            'text/json',                         // .json
            'application/epub+zip',              // .epub
            
            // Archive formats (where supported)
            'application/zip',                   // .zip
            'application/x-zip-compressed',      // .zip
            
            // Legacy formats
            'application/vnd.wordperfect',       // .wpd
            'application/x-abiword'              // .abw
        ];
        
        // Check file type
        if (!allowedTypes.includes(file.type)) {
            // Fallback: check file extension for additional support
            const fileName = file.name.toLowerCase();
            const supportedExtensions = [
                '.pdf', '.doc', '.docx', '.xlsx', '.xls', '.pptx', '.ppt',
                '.txt', '.md', '.html', '.htm', '.csv', '.tsv', '.rtf',
                '.png', '.jpg', '.jpeg', '.tiff', '.tif', '.bmp', '.webp', '.gif', '.svg',
                '.eml', '.msg', '.odt', '.ods', '.odp', '.xml', '.json', '.epub',
                '.asciidoc', '.adoc', '.zip'
            ];
            
            const hasValidExtension = supportedExtensions.some(ext => fileName.endsWith(ext));
            
            if (!hasValidExtension) {
                return 'File type not supported for contract analysis. Supported formats include PDF, Word, Excel, PowerPoint, text files, images, emails, and many other document types.';
            }
        }
        
        const maxSize = 50 * 1024 * 1024; // 50MB
        if (file.size > maxSize) {
            return 'File size must be less than 50MB';
        }
        
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const fileError = validateFile(file);
        if (fileError) {
            setError(fileError);
            return;
        }
        
        console.log('🔄 Setting upload state to extracting for dynamic UI');
        setUploadState('extracting'); // Start with extracting state to show dynamic UI immediately
        setError('');
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('immediate', 'true'); // Request immediate analysis_id
            if (reAnalysisGuidance) {
                formData.append('guidance', reAnalysisGuidance);
            }
            
            console.log('🚀 Starting contract analysis with immediate response...');
            
            const response = await axiosInstance.post('/api/stock/contracts/upload/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            console.log('✅ Immediate response received:', response.data);
            
            // Handle immediate response (analysis started in background)
            if (response.data.status === 'processing' && response.data.analysis_id) {
                console.log('🚀 Analysis started in background, analysis ID:', response.data.analysis_id);
                
                // Set analysis ID immediately so progress polling can start
                setAnalysisId(response.data.analysis_id);
                
                // Start polling for completion
                const analysisId = response.data.analysis_id;
                let pollAttempts = 0;
                const maxPollAttempts = 300; // 5 minutes at 1 second intervals
                
                const pollForCompletion = async () => {
                    try {
                        const progressResponse = await axiosInstance.get(`/api/stock/contracts/progress/${analysisId}/`);
                        const { is_complete, status, extracted_data, error_message, requires_manual_product_type, current_page, total_pages } = progressResponse.data;
                        
                        console.log(`📊 Poll attempt ${pollAttempts + 1}: Status=${status}, Page=${current_page}/${total_pages}, Complete=${is_complete}`);
                        
                        // Page progress is already being updated by the useEffect polling hook
                        
                        if (is_complete) {
                            console.log('🎉 Analysis completed!');
                            
                            if (status === 'failed') {
                                throw new Error(error_message || 'Analysis failed');
                            }
                            
                            // Check if manual product type selection is required
                            if (requires_manual_product_type) {
                                console.log('🚨 Manual product type selection required!');
                                setExtractedData(extracted_data || {});
                                setShowManualProductType(true);
                                setUploadState('manual_product_type');
                                return; // Stop polling
                            }
                            
                            // Analysis completed successfully
                            const validExtractedData = extracted_data || {};
                            setExtractedData(validExtractedData);
                            setUploadState('results');
                            
                            // Initialize selected fields (only non-null fields checked by default)
                            const initialSelected = {};
                            Object.keys(validExtractedData).forEach(key => {
                                if (validExtractedData[key] !== null) {
                                    initialSelected[key] = true;
                                }
                            });
                            setSelectedFields(initialSelected);
                            
                            return; // Stop polling
                        }
                        
                        // Continue polling
                        pollAttempts++;
                        if (pollAttempts < maxPollAttempts) {
                            setTimeout(pollForCompletion, 1000); // Poll every second
                        } else {
                            throw new Error('Analysis timeout - processing took too long');
                        }
                        
                    } catch (error) {
                        console.error('❌ Polling error:', error);
                        setError(getErrorMessage(error, 'File could not be processed. Please check the file and try again.'));
                        setUploadState('idle');
                    }
                };
                
                // Start polling
                setTimeout(pollForCompletion, 1000); // Start after 1 second
                return;
            }
            
            // Handle legacy synchronous response (fallback)
            console.log('📄 Legacy synchronous response handling');
            console.log('🔍 Checking requires_manual_product_type:', response.data.requires_manual_product_type);
            
            // Check if manual product type selection is required
            if (response.data.requires_manual_product_type) {
                console.log('🚨 Manual product type selection required!');
                console.log('📋 Analysis ID:', response.data.analysis_id);
                console.log('📊 Extracted data:', response.data.extracted_data);
                
                setAnalysisId(response.data.analysis_id);
                setExtractedData(response.data.extracted_data);
                setShowManualProductType(true);
                setUploadState('manual_product_type');
                
                console.log('🎯 Modal state set - showManualProductType should be true');
                console.log('🎯 Upload state set to:', 'manual_product_type');
                
                showError('No product type found in contract. Please select manually.');
                return;
            } else {
                console.log('✅ Product type was found, proceeding with normal flow');
            }
            
            // Normal flow - set up results state
            setAnalysisId(response.data.analysis_id);
            setExtractedData(response.data.extracted_data);
            
            // Initialize selected fields (all checked by default)
            const initialSelection = {};
            Object.keys(response.data.extracted_data).forEach(key => {
                if (response.data.extracted_data[key] !== null) {
                    initialSelection[key] = true;
                }
            });
            setSelectedFields(initialSelection);
            
            setUploadState('results');
            
        } catch (error) {
            console.error('❌ Contract analysis error:', error);
            
            // Handle different error types
            setError(getErrorMessage(error, 'File could not be processed. Please check the file and try again.'));
            
            setUploadState('error');
        }
    };

    const handleClose = async () => {
        // If we have an analysis ID and are closing/canceling, discard the analysis
        if (analysisId && (uploadState === 'results' || uploadState === 'manual_product_type')) {
            try {
                console.log('🗑️ Auto-discarding contract analysis on close:', analysisId);
                await axiosInstance.delete(`/api/stock/contracts/discard/${analysisId}/`);
                console.log('✅ Contract analysis auto-discarded on close');
            } catch (error) {
                console.error('❌ Failed to auto-discard contract analysis:', error);
                // Don't block closing if discard fails
            }
        }
        
        setFile(null);
        setError('');
        setUploadState('idle');
        setExtractedData(null);
        setSelectedFields({});
        setAnalysisId(null);
        setReAnalysisGuidance('');
        setShowManualProductType(false);
        setSelectedProductType('');
        onClose();
    };

    const handleFieldToggle = (fieldName) => {
        setSelectedFields(prev => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    };

    const handleDiscard = async () => {
        // If we have an analysis ID, we need to discard it properly from backend
        if (analysisId) {
            try {
                console.log('🗑️ Discarding contract analysis:', analysisId);
                showSuccess('Discarding contract analysis...');
                
                await axiosInstance.delete(`/api/stock/contracts/discard/${analysisId}/`);
                
                console.log('✅ Contract analysis discarded successfully');
                showSuccess('Contract analysis discarded successfully');
            } catch (error) {
                console.error('❌ Failed to discard contract analysis:', error);
                
                // Handle different error types
                if (error.response?.status === 404) {
                    console.log('ℹ️ Analysis already deleted or not found');
                    // Continue with close anyway
                } else {
                    showError('Failed to discard contract analysis. Please try again.');
                    return; // Don't close modal if discard failed
                }
            }
        }
        
        handleClose();
    };

    const handleApply = () => {
        // Get selected data for stock item creation
        const selectedData = {};
        Object.keys(selectedFields).forEach(key => {
            if (selectedFields[key] && extractedData[key]) {
                selectedData[key] = extractedData[key];
            }
        });
        
        console.log('📋 Applying selected contract data:', selectedData);
        onSuccess({ selectedData, analysisId });
        // Don't close the modal here - let the parent component handle the workflow
    };

    const handleReAnalyze = async (guidance) => {
        if (!analysisId) return;
        
        setUploadState('extracting');
        setError('');
        
        try {
            console.log('🔄 Re-analyzing contract with guidance:', guidance);
            
            const response = await axiosInstance.post(`/api/stock/contracts/reanalyze/${analysisId}/`, {
                guidance: guidance
            });
            
            console.log('✅ Re-analysis completed:', response.data);
            
            setExtractedData(response.data.extracted_data);
            
            // Re-initialize selected fields
            const initialSelection = {};
            Object.keys(response.data.extracted_data).forEach(key => {
                if (response.data.extracted_data[key] !== null) {
                    initialSelection[key] = true;
                }
            });
            setSelectedFields(initialSelection);
            
            setUploadState('results');
            
        } catch (error) {
            console.error('❌ Re-analysis error:', error);
            
            setError(getErrorMessage(error, 'File could not be processed. Please check the file and try again.'));
            
            setUploadState('error');
        }
    };

    const handleManualProductTypeSubmit = async () => {
        if (!selectedProductType) {
            showError('Please select a product type');
            return;
        }

        setUploadState('uploading_manual');
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('product_type', selectedProductType);
            
            console.log('🔄 Completing upload with manual product type:', selectedProductType);
            
            const response = await axiosInstance.post(
                `/api/stock/contracts/complete/${analysisId}/`, 
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            
            console.log('✅ Manual completion successful:', response.data);
            
            // Update extracted data with manual product type
            const updatedExtractedData = {
                ...response.data.extracted_data,
                product_type: selectedProductType
            };
            setExtractedData(updatedExtractedData);
            
            // Initialize selected fields
            const initialSelection = {};
            Object.keys(updatedExtractedData).forEach(key => {
                if (updatedExtractedData[key] !== null) {
                    initialSelection[key] = true;
                }
            });
            setSelectedFields(initialSelection);
            
            setShowManualProductType(false);
            setUploadState('results');
            showSuccess('Contract uploaded successfully with manual product type selection');
            
        } catch (error) {
            console.error('❌ Manual completion error:', error);
            setError('Failed to complete upload. Please try again.');
            setUploadState('error');
            showError('Failed to complete upload. Please try again.');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content contract-upload-modal">
                <div className="modal-header">
                    <h2>
                        {uploadState === 'results' ? 'Contract Analysis Results' : 'Upload Contract Document'}
                    </h2>
                    <button 
                        className="close-button"
                        onClick={handleClose}
                        disabled={uploadState === 'extracting'}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                {/* Upload State - File Selection */}
                {uploadState === 'idle' && (
                    <form onSubmit={handleSubmit} className="upload-form">
                        <FileDropZone
                            className={`file-drop-zone ${file ? 'has-file' : ''}`}
                            activeClassName="active"
                            onFiles={(files) => acceptDroppedFile(files[0])}
                            onError={setError}
                        >
                            {file ? (
                                <div className="file-selected">
                                    <FontAwesomeIcon icon={faFileText} className="file-icon" />
                                    <div className="file-info">
                                        <span className="file-name">{file.name}</span>
                                        <span className="file-size">
                                            {(file.size / 1024 / 1024).toFixed(2)} MB
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="remove-file"
                                        onClick={() => setFile(null)}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                            ) : (
                                <div className="drop-zone-content">
                                    <FontAwesomeIcon icon={faUpload} className="upload-icon" />
                                    <p className="drop-text">
                                        Drag and drop your contract document here, or{' '}
                                        <label className="file-select-link">
                                            browse files
                                            <input
                                                type="file"
                                                onChange={handleFileChange}
                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.html,.htm,.xhtml,.csv,.tsv,.rtf,.png,.jpg,.jpeg,.tiff,.tif,.bmp,.webp,.gif,.svg,.eml,.msg,.odt,.ods,.odp,.xml,.json,.epub,.asciidoc,.adoc,.zip,.wpd,.abw"
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </p>
                                    <p className="file-requirements">
                                        Supported formats: PDF, Word, Excel, PowerPoint, text files, images, emails, OpenDocument, and many more (max 50MB)
                                    </p>
                                </div>
                            )}
                        </FileDropZone>
                        
                        <div className="upload-info">
                            <h3>AI-Powered Contract Analysis</h3>
                            <ul>
                                <li>Document will be stored securely in DocuWare</li>
                                <li>AI will extract product type, country, supplier, and pricing</li>
                                <li>You can review and select which fields to use</li>
                                <li>Stock items and variants will be created with extracted data</li>
                            </ul>
                        </div>
                        
                        <div className="modal-actions centered">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={!file}
                            >
                                <FontAwesomeIcon icon={faRobot} />
                                Analyze Contract
                            </button>
                        </div>
                    </form>
                )}

                {/* Loading States */}
                {uploadState === 'extracting' && (
                    <div className="loading-state">
                        <div className="loading-content">
                            
                            <>
                                    <div className="extraction-progress" style={{
                                        textAlign: 'center',
                                        marginBottom: '1.5rem'
                                    }}>
                                        <p className="current-step" style={{
                                            fontSize: '1rem',
                                            fontWeight: '500',
                                            color: '#374151',
                                            margin: '0 0 0.75rem 0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '0.5rem',
                                            opacity: 1,
                                            transition: 'opacity 0.5s ease-in-out',
                                            minHeight: '1.5rem'
                                        }}>
                                            <FontAwesomeIcon 
                                                icon={faRobot} 
                                                className="ai-icon" 
                                                style={{ color: '#3b82f6', fontSize: '1.1rem' }}
                                            />
                                            {extractionSteps[extractionStep]}
                                        </p>
                                        
                                        {/* Page Progress Display */}
                                        {pageProgress.total > 0 && (
                                            <div className="page-progress" style={{
                                                fontSize: '0.9rem',
                                                color: '#6b7280',
                                                fontWeight: '500',
                                                marginTop: '0.5rem'
                                            }}>
                                                Extracting data from page {pageProgress.current}/{pageProgress.total}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="timing-info" style={{
                                        marginTop: '1.5rem',
                                        textAlign: 'center'
                                    }}>
                                        <div className="timing-details" style={{
                                            marginBottom: '1rem'
                                        }}>
                                            {file && file.size > 10 * 1024 * 1024 ? (
                                                <div className="large-file-notice" style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                    padding: '0.75rem',
                                                    backgroundColor: '#fef3c7',
                                                    border: '1px solid #f59e0b',
                                                    borderRadius: '0.375rem',
                                                    color: '#92400e'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <FontAwesomeIcon 
                                                            icon={faExclamationTriangle} 
                                                            className="warning-icon" 
                                                            style={{ color: '#f59e0b' }}
                                                        />
                                                        <span style={{ fontWeight: '500' }}>
                                                            Large file detected ({(file.size / 1024 / 1024).toFixed(1)}MB)
                                                        </span>
                                                    </div>
                                                    <small style={{ fontSize: '0.8rem' }}>
                                                        Processing may take up to 2 minutes for accurate extraction
                                                    </small>
                                                </div>
                                            ) : (
                                                <div className="standard-timing" style={{
                                                    padding: '0.5rem',
                                                    backgroundColor: '#f0f9ff',
                                                    border: '1px solid #3b82f6',
                                                    borderRadius: '0.375rem',
                                                    color: '#1e40af'
                                                }}>
                                                    <small style={{ fontWeight: '500' }}>
                                                        Typical processing time: 30-60 seconds
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="extraction-details" style={{
                                            padding: '0.75rem',
                                            backgroundColor: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '0.375rem',
                                            color: '#64748b',
                                            fontSize: '0.8rem',
                                            lineHeight: '1.4'
                                        }}>
                                            <small>
                                                Our AI analyzes document structure, applies OCR for scanned content, 
                                                and extracts key contract details including product type, quantities, 
                                                pricing, and specifications.
                                            </small>
                                        </div>
                                    </div>
                                </>
                        </div>
                    </div>
                )}

                {/* Results State */}
                {uploadState === 'results' && extractedData && (
                    <ContractExtractionResults
                        extractedData={extractedData}
                        selectedFields={selectedFields}
                        onFieldToggle={handleFieldToggle}
                        onApply={handleApply}
                        onDiscard={handleDiscard}
                        onReAnalyze={handleReAnalyze}
                        reAnalysisGuidance={reAnalysisGuidance}
                        setReAnalysisGuidance={setReAnalysisGuidance}
                        isReAnalyzing={uploadState === 'extracting'}
                    />
                )}

                {/* Manual Product Type Selection Modal */}
                {showManualProductType && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2>Product Type Required</h2>
                            </div>
                            <div className="modal-body">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f59e0b', fontSize: '1.25rem' }} />
                                    <p style={{ margin: 0, fontWeight: '600', color: '#92400e' }}>
                                        Product type selection is required to upload the contract to DocuWare.
                                    </p>
                                </div>
                                <p style={{ marginBottom: '1.5rem', color: '#6b7280' }}>
                                    AI analysis completed but no product type was detected. Please select the product type manually:
                                </p>
                                <div className="product-type-selection">
                                    {productTypes.map(type => (
                                        <button
                                            key={type.value}
                                            className={`product-type-button ${selectedProductType === type.value ? 'selected' : ''}`}
                                            onClick={() => setSelectedProductType(type.value)}
                                        >
                                            {type.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="modal-actions" style={{
                                    display: 'flex',
                                    gap: '0.75rem',
                                    justifyContent: 'center',
                                    marginTop: '1.5rem'
                                }}>
                                    <button
                                        className="btn btn-secondary"
                                        onClick={handleClose}
                                        disabled={uploadState === 'uploading_manual'}
                                        type="button"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            minWidth: '120px',
                                            justifyContent: 'center',
                                            padding: '0.75rem 1rem',
                                            backgroundColor: '#ffffff',
                                            color: '#475569',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '0.375rem',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            cursor: uploadState === 'uploading_manual' ? 'not-allowed' : 'pointer',
                                            opacity: uploadState === 'uploading_manual' ? 0.6 : 1,
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (uploadState !== 'uploading_manual') {
                                                e.target.style.backgroundColor = '#f8fafc';
                                                e.target.style.borderColor = '#cbd5e1';
                                                e.target.style.transform = 'translateY(-1px)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (uploadState !== 'uploading_manual') {
                                                e.target.style.backgroundColor = '#ffffff';
                                                e.target.style.borderColor = '#e2e8f0';
                                                e.target.style.transform = 'translateY(0)';
                                            }
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.875rem' }} />
                                        Cancel
                                    </button>
                                    
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleManualProductTypeSubmit}
                                        disabled={!selectedProductType || uploadState === 'uploading_manual'}
                                        type="button"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            minWidth: '120px',
                                            justifyContent: 'center',
                                            padding: '0.75rem 1rem',
                                            background: (!selectedProductType || uploadState === 'uploading_manual') ? 
                                                '#94a3b8' : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '0.375rem',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            cursor: (!selectedProductType || uploadState === 'uploading_manual') ? 'not-allowed' : 'pointer',
                                            opacity: (!selectedProductType || uploadState === 'uploading_manual') ? 0.6 : 1,
                                            transition: 'all 0.2s ease',
                                            boxShadow: (!selectedProductType || uploadState === 'uploading_manual') ? 
                                                'none' : '0 4px 12px rgba(59, 130, 246, 0.4)'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (selectedProductType && uploadState !== 'uploading_manual') {
                                                e.target.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
                                                e.target.style.transform = 'translateY(-1px)';
                                                e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.5)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (selectedProductType && uploadState !== 'uploading_manual') {
                                                e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                                                e.target.style.transform = 'translateY(0)';
                                                e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                                            }
                                        }}
                                    >
                                        {uploadState === 'uploading_manual' ? (
                                            <>
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '0.875rem' }} />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faUpload} style={{ fontSize: '0.875rem' }} />
                                                Upload to DocuWare
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContractUploadModal; 